/**
 * Transcript Editor Component
 * 
 * Allows viewing and editing of transcribed handwriting.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface Transcript {
  id: string;
  question_id: string;
  text: string;
  confidence: number;
  quality_flags: string[];
  human_edited: boolean;
  version: number;
}

interface TranscriptEditorProps {
  transcript?: Transcript;
  onSave: (text: string) => Promise<void>;
  saving?: boolean;
}

const FLAG_LABELS: Record<string, string> = {
  blurry: '模糊',
  partial: '不完整',
  unreadable: '無法辨識',
  low_contrast: '對比度低',
  crossed_out: '已劃掉',
  multiple_attempts: '多次嘗試',
};

export function TranscriptEditor({
  transcript,
  onSave,
  saving = false,
}: TranscriptEditorProps) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    if (transcript) {
      setEditText(transcript.text);
    }
  }, [transcript]);

  const handleStartEdit = () => {
    setEditing(true);
    setEditText(transcript?.text || '');
  };

  const handleCancel = () => {
    setEditing(false);
    setEditText(transcript?.text || '');
  };

  const handleSave = async () => {
    await onSave(editText);
    setEditing(false);
  };

  if (!transcript) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          沒有找到轉錄內容
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          轉錄內容
        </Typography>
        <Chip
          label={`信心度: ${Math.round(transcript.confidence * 100)}%`}
          color={transcript.confidence >= 0.8 ? 'success' : 'warning'}
          size="small"
        />
        {transcript.human_edited && (
          <Chip label="已人工編輯" color="info" size="small" />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {!editing && (
          <Button
            startIcon={<EditIcon />}
            onClick={handleStartEdit}
            variant="outlined"
            size="small"
          >
            編輯
          </Button>
        )}
      </Stack>

      {/* Quality flags */}
      {transcript.quality_flags && transcript.quality_flags.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            品質問題:{' '}
            {transcript.quality_flags
              .map((f) => FLAG_LABELS[f] || f)
              .join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Content */}
      {editing ? (
        <Box>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="輸入轉錄內容..."
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: '"Noto Serif TC", serif',
                fontSize: '1.1rem',
                lineHeight: 1.8,
              },
            }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              保存
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={saving}
            >
              取消
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            minHeight: 150,
            fontFamily: '"Noto Serif TC", serif',
            fontSize: '1.1rem',
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
          }}
        >
          {transcript.text || (
            <Typography color="text.secondary" fontStyle="italic">
              (空白)
            </Typography>
          )}
        </Box>
      )}

      {/* Metadata */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          版本: {transcript.version}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          題目: {transcript.question_id}
        </Typography>
      </Stack>
    </Paper>
  );
}

